/**
 * useVoiceSession — orchestrates a voice conversation from the browser:
 *   1. request a pre-signed WS URL from the presigner
 *   2. open the WebSocket to the AgentCore agent
 *   3. stream mic PCM16 frames (binary) to the agent
 *   4. play assistant audio (binary) coming back
 *   5. surface a speaking signal (for the talking-logo animation) / status via
 *      {type,data} JSON control messages
 *
 * Transcript is delivered to the screenshare app (via the session hub), not the
 * voice socket, so this hook no longer collects or exposes it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getPresignedWsUrl } from '../lib/presigner';
import { MicCapture, AudioPlayer, MicRingBuffer } from '../lib/audio';

export type SessionStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'error'
  | 'ended';

/**
 * Pure liveness decision for a (possibly standby) voice socket. A socket counts
 * as "live" once it is OPEN and either the first inbound assistant audio frame
 * has arrived (a positive signal the server orchestrator started streaming) or
 * a short settle grace has elapsed. On a `speakFirst:false` resume the model may
 * stay silent until the user speaks, so the settle timer is the expected trigger
 * and first-inbound-audio is only an earlier promotion. See design B4.
 */
export function isConnectionLive(args: {
  open: boolean;
  firstInboundAudio: boolean;
  settleElapsed: boolean;
}): boolean {
  return args.open && (args.firstInboundAudio || args.settleElapsed);
}

/**
 * Decide what a socket's `onclose` should do, as a pure function of the three
 * facts the hook knows at close time (design B5/B6):
 *   - intentional (user stop / unmount / deliberate old-socket close) → 'ended'
 *   - a non-active (old/standby) socket closing during normal operation → 'noop'
 *   - the ACTIVE socket closing unexpectedly WITH a standby present (warming OR
 *     live) → 'promote-standby' (promote/await it rather than racing a reactive
 *     reconnect that would open a third socket)
 *   - the ACTIVE socket closing unexpectedly with NO standby → 'reconnecting'
 *
 * `hasStandby` means ANY standby (warming OR live), so 'promote-standby' wins
 * over 'reconnecting'.
 */
export function nextStatusOnClose(args: {
  intentional: boolean;
  wasActive: boolean;
  hasStandby: boolean;
}): 'ended' | 'reconnecting' | 'promote-standby' | 'noop' {
  if (args.intentional) return 'ended';
  if (!args.wasActive) return 'noop';
  if (args.hasStandby) return 'promote-standby';
  return 'reconnecting';
}

/** True when the UI should surface the "reconnecting…" cue for a given status. */
export function shouldShowReconnectCue(status: SessionStatus): boolean {
  return status === 'reconnecting';
}

/**
 * Keepalive interval. Without any client→server traffic the voice socket is
 * idle-reaped by AgentCore after ~16-24s, forcing constant reconnects. A
 * periodic ping (a no-op control message on the agent's voice branch) keeps it
 * open. Mic audio flows continuously while live, but the ping also covers gaps
 * (e.g. mic start failure) and matches the screenshare keepalive.
 */
const PING_INTERVAL_MS = 20_000;

/**
 * Proactively swap the voice socket this long after connect — comfortably under
 * AgentCore's ~60-min hard cap so the new socket is live before the old one is
 * force-closed. See design B1.
 */
const PROACTIVE_SWAP_MS = 55 * 60_000; // 3_300_000
/**
 * If the pre-opened standby socket does not reach live within this window,
 * abort the proactive swap and let the reactive path handle the eventual hard
 * close. Bounds how long the client waits to promote a warming standby (the
 * server heartbeat keeps the standby alive — design B7).
 */
const PREOPEN_LIVE_TIMEOUT_MS = 10_000;
/**
 * Grace after a socket's `open` before it counts as live, when no inbound audio
 * has arrived yet (the expected trigger on a speakFirst:false resume). Short so
 * promotion is near-instant but long enough to let an immediate server reject
 * surface as a close first.
 */
const LIVE_SETTLE_MS = 750;
/** Reactive reconnect backoff (mirrors the screenshare hook). */
const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const BACKOFF_MULTIPLIER = 2;

/**
 * A single voice socket plus its per-socket lifecycle state. Two can coexist
 * briefly during a proactive swap: the `active` one the mic sends to, and a
 * `standby` pre-opened ahead of the hard cap. See design B2.
 */
interface VoiceConnection {
  ws: WebSocket;
  pingTimer: ReturnType<typeof setInterval> | null;
  /** Reached live (open + first inbound audio or settle). */
  live: boolean;
  role: 'active' | 'standby';
  /** First inbound assistant-audio frame seen on this socket (design B4). */
  firstInboundAudio: boolean;
  /** LIVE_SETTLE_MS grace elapsed since this socket's open (design B4). */
  settleElapsed: boolean;
  /** Settle grace timer; cleared on live/promote/teardown. */
  settleTimer: ReturnType<typeof setTimeout> | null;
  /** Deliberate per-socket close (e.g. the old socket during a promotion):
   *  its `onclose` must be a no-op and never schedule a reconnect. */
  intentional: boolean;
}

/**
 * The subset of `AudioPlayer` the message dispatcher drives. Kept as a narrow
 * interface so the pure `handleVoiceMessage` seam can be unit-tested against a
 * lightweight stub without a real AudioContext (the voice test setup runs in a
 * plain `node` environment — no jsdom / Web Audio).
 */
export interface VoicePlayer {
  play(pcm16: ArrayBuffer): void;
  flush(): void;
}

/**
 * Handle one inbound voice-socket frame the way `ws.onmessage` does:
 *   - binary (ArrayBuffer)  → assistant audio → player.play(...)
 *   - `{ type: 'barge_in' }` → user interruption → player.flush()
 *     (flush forces the player's speaking signal to false)
 *   - anything else / malformed → ignored
 *
 * Extracted as a pure function so the audio/barge-in wiring can be tested
 * directly (see useVoiceSession.test.ts) without opening a real WebSocket,
 * microphone, or AudioContext. Never throws on malformed control frames.
 */
export function handleVoiceMessage(data: unknown, player: VoicePlayer): void {
  if (data instanceof ArrayBuffer) {
    player.play(data); // assistant audio
    return;
  }
  if (typeof data !== 'string') return;
  try {
    const msg = JSON.parse(data) as { type?: unknown };
    if (msg && typeof msg === 'object' && msg.type === 'barge_in') {
      // Agent detected user interruption (Nova Sonic INTERRUPTED) — drop all
      // buffered/scheduled assistant audio so the bot stops talking.
      // player.flush() forces the speaking signal to false.
      player.flush();
    }
  } catch {
    /* ignore malformed control frames */
  }
}

export function useVoiceSession() {
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [speaking, setSpeaking] = useState(false);

  // Audio stays alive for the whole call (no getUserMedia re-prompt on a swap):
  // the mic and player are created ONCE on the first start() and only the socket
  // underneath them changes (design B2).
  const micRef = useRef<MicCapture | null>(null);
  const playerRef = useRef<AudioPlayer | null>(null);
  /** Unsubscribe from the player's speaking-change fan-out; cleared on teardown. */
  const speakingUnsubRef = useRef<(() => void) | null>(null);

  // Two sockets can coexist briefly during a proactive swap. The mic send target
  // is indirected through activeRef, never a captured ws.
  const activeRef = useRef<VoiceConnection | null>(null);
  const standbyRef = useRef<VoiceConnection | null>(null);

  const sessionIdRef = useRef<string | null>(null);
  /** Call-level deliberate close (user stop / unmount): suppress all reconnects. */
  const intentionalCloseRef = useRef(false);
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const proactiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Bounds how long we wait to promote a warming standby (PREOPEN_LIVE_TIMEOUT_MS). */
  const preOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Retains mic PCM across the sub-second cutover / reactive gap (design B3). */
  const micBufferRef = useRef<MicRingBuffer>(new MicRingBuffer());

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearProactiveTimer = useCallback(() => {
    if (proactiveTimerRef.current !== null) {
      clearTimeout(proactiveTimerRef.current);
      proactiveTimerRef.current = null;
    }
  }, []);

  const clearPreOpenTimeout = useCallback(() => {
    if (preOpenTimeoutRef.current !== null) {
      clearTimeout(preOpenTimeoutRef.current);
      preOpenTimeoutRef.current = null;
    }
  }, []);

  /** Detach handlers + stop timers for a single connection, then (optionally)
   *  close its socket. Marks it intentional so a pending onclose is a no-op. */
  const teardownConnection = useCallback((conn: VoiceConnection, close: boolean) => {
    conn.intentional = true;
    if (conn.pingTimer !== null) {
      clearInterval(conn.pingTimer);
      conn.pingTimer = null;
    }
    if (conn.settleTimer !== null) {
      clearTimeout(conn.settleTimer);
      conn.settleTimer = null;
    }
    const ws = conn.ws;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (close && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      ws.close();
    }
  }, []);

  /** Mic send indirection: send to the active socket when OPEN, else buffer. */
  const sendMic = useCallback((pcm16: ArrayBuffer) => {
    const active = activeRef.current;
    if (active && active.ws.readyState === WebSocket.OPEN) {
      active.ws.send(pcm16);
    } else {
      micBufferRef.current.push(pcm16);
    }
  }, []);

  // connectInternal / preOpen / promote reference each other; a ref breaks the
  // declaration cycle without reordering the useCallbacks.
  const connectInternalRef = useRef<() => Promise<void>>(async () => {});
  const armProactiveRef = useRef<() => void>(() => {});

  /** Exponential-backoff reactive reconnect (mirrors useDisplayChannel.ts). */
  const scheduleReactiveReconnect = useCallback(() => {
    if (intentionalCloseRef.current) return;
    clearReconnectTimer();
    const delay = backoffRef.current;
    backoffRef.current = Math.min(backoffRef.current * BACKOFF_MULTIPLIER, MAX_BACKOFF_MS);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      void connectInternalRef.current();
    }, delay);
  }, [clearReconnectTimer]);

  /**
   * Promote the current standby to active: rebind the mic (via activeRef),
   * flush the mic ring buffer into the new socket, close the old active socket,
   * keep the player alive, re-arm the proactive timer. Status stays 'live'.
   */
  const promoteStandby = useCallback(() => {
    const standby = standbyRef.current;
    if (!standby) return;
    clearPreOpenTimeout();
    if (standby.settleTimer !== null) {
      clearTimeout(standby.settleTimer);
      standby.settleTimer = null;
    }

    const oldActive = activeRef.current;
    standby.role = 'active';
    standby.live = true;
    activeRef.current = standby;
    standbyRef.current = null;

    // Flush any mic frames buffered during the sub-second cutover into the new
    // socket before resuming live sends.
    if (standby.ws.readyState === WebSocket.OPEN) {
      for (const frame of micBufferRef.current.drain()) {
        standby.ws.send(frame);
      }
    }

    // Close the old socket deliberately so its onclose is a no-op. Player/mic/
    // AudioContext are untouched — gapless.
    if (oldActive) teardownConnection(oldActive, true);

    // The seam is not surfaced; keep 'live'.
    setStatus((s) => (s === 'live' ? s : 'live'));
    backoffRef.current = INITIAL_BACKOFF_MS;
    // Re-arm the proactive window on the newly-active socket.
    armProactiveRef.current();
  }, [clearPreOpenTimeout, teardownConnection]);

  /**
   * Evaluate a (standby) connection's liveness; promote when live. Also used as
   * the settle/first-audio callback. No-op if the connection is no longer the
   * current standby or has been torn down.
   */
  const evaluateStandbyLive = useCallback(() => {
    const standby = standbyRef.current;
    if (!standby || standby.intentional) return;
    const open = standby.ws.readyState === WebSocket.OPEN;
    if (
      isConnectionLive({
        open,
        firstInboundAudio: standby.firstInboundAudio,
        settleElapsed: standby.settleElapsed,
      })
    ) {
      promoteStandby();
    }
  }, [promoteStandby]);

  /** Wire onopen/onmessage/onerror/onclose + ping + settle for a connection. */
  const wireConnection = useCallback(
    (conn: VoiceConnection) => {
      const ws = conn.ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'hello', data: { channel: 'voice' } }));
        // Keepalive ping per connection (so a warming standby also pings).
        if (conn.pingTimer !== null) clearInterval(conn.pingTimer);
        conn.pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping', data: {} }));
          }
        }, PING_INTERVAL_MS);

        if (conn.role === 'active') {
          // The mic send target is activeRef, already set — just mark live.
          conn.live = true;
          setStatus('live');
          // A successful (re)open resets the backoff for the next disconnect.
          backoffRef.current = INITIAL_BACKOFF_MS;
          // Flush anything buffered during a reactive gap into the new socket.
          for (const frame of micBufferRef.current.drain()) {
            if (ws.readyState === WebSocket.OPEN) ws.send(frame);
          }
          // Arm the proactive swap window on this socket.
          armProactiveRef.current();
        } else {
          // Standby: start the settle grace; promote on settle or first audio.
          conn.settleTimer = setTimeout(() => {
            conn.settleTimer = null;
            conn.settleElapsed = true;
            evaluateStandbyLive();
          }, LIVE_SETTLE_MS);
        }
      };

      ws.onmessage = (evt: MessageEvent) => {
        if (evt.data instanceof ArrayBuffer && !conn.firstInboundAudio) {
          conn.firstInboundAudio = true;
          if (conn.role === 'standby') evaluateStandbyLive();
        }
        // Route BOTH sockets to the one shared player (defensive — a late frame
        // on either socket is harmless).
        const player = playerRef.current;
        if (player) handleVoiceMessage(evt.data, player);
      };

      ws.onerror = () => {
        // A close event normally follows onerror and drives the decision; don't
        // flip to a hard error for a transient blip we intend to recover.
        if (intentionalCloseRef.current) {
          setStatus('error');
          setError('WebSocket error');
        }
      };

      ws.onclose = (evt: CloseEvent) => {
        if (conn.pingTimer !== null) {
          clearInterval(conn.pingTimer);
          conn.pingTimer = null;
        }
        if (conn.settleTimer !== null) {
          clearTimeout(conn.settleTimer);
          conn.settleTimer = null;
        }

        const wasActive = activeRef.current === conn;
        const hasStandby = standbyRef.current !== null && standbyRef.current !== conn;
        const action = nextStatusOnClose({
          intentional: intentionalCloseRef.current || conn.intentional,
          wasActive,
          hasStandby,
        });

        if (action === 'noop') {
          // An old/standby socket closing; clear its ref if it is the standby.
          if (standbyRef.current === conn) standbyRef.current = null;
          return;
        }

        if (action === 'ended') {
          // Deliberate (call-level) close: full teardown is handled by stop()/
          // unmount. If this was a per-socket intentional close, just drop refs.
          if (activeRef.current === conn) activeRef.current = null;
          if (standbyRef.current === conn) standbyRef.current = null;
          return;
        }

        // From here the ACTIVE socket closed unexpectedly. Keep player/mic/
        // AudioContext alive across the transient close.
        activeRef.current = null;
        if (evt.code !== 1000 && evt.reason) {
          setError(`connection closed: ${evt.reason}`);
        }

        if (action === 'promote-standby') {
          // Await/promote the warming-or-live standby rather than racing a
          // reactive reconnect. Bounded by the remaining pre-open window.
          const standby = standbyRef.current;
          if (standby) {
            standby.role = 'active';
            // If it is already live, promote now; otherwise its settle/first-
            // audio will promote it. Keep status 'live' (seam masked).
            setStatus((s) => (s === 'live' ? s : 'live'));
            evaluateStandbyLive();
            return;
          }
          // Lost the standby in the meantime → fall through to reconnect.
        }

        // 'reconnecting': no standby to promote. Show the cue + backoff.
        setStatus('reconnecting');
        scheduleReactiveReconnect();
      };
    },
    [evaluateStandbyLive, scheduleReactiveReconnect],
  );

  /** Open a standby socket ahead of the hard cap and promote it when live. */
  const preOpen = useCallback(async () => {
    if (intentionalCloseRef.current) return;
    const sessionId = sessionIdRef.current;
    if (sessionId === null) return;
    // Already have a standby in flight.
    if (standbyRef.current !== null) return;

    let wsUrl: string;
    try {
      ({ wsUrl } = await getPresignedWsUrl(sessionId));
    } catch {
      // Keep the active socket; re-arm a proactive retry.
      armProactiveRef.current();
      return;
    }
    if (intentionalCloseRef.current) return;

    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    const conn: VoiceConnection = {
      ws,
      pingTimer: null,
      live: false,
      role: 'standby',
      firstInboundAudio: false,
      settleElapsed: false,
      settleTimer: null,
      intentional: false,
    };
    standbyRef.current = conn;
    wireConnection(conn);

    // Bound how long we wait to promote a warming standby. On timeout: discard
    // it and re-arm a proactive retry (the reactive path is the backstop).
    clearPreOpenTimeout();
    preOpenTimeoutRef.current = setTimeout(() => {
      preOpenTimeoutRef.current = null;
      const standby = standbyRef.current;
      if (standby === conn && !standby.live) {
        standbyRef.current = null;
        teardownConnection(standby, true);
        armProactiveRef.current();
      }
    }, PREOPEN_LIVE_TIMEOUT_MS);
  }, [wireConnection, clearPreOpenTimeout, teardownConnection]);

  /** (Re)arm the proactive swap timer on the currently-active socket. */
  const armProactive = useCallback(() => {
    clearProactiveTimer();
    if (intentionalCloseRef.current) return;
    proactiveTimerRef.current = setTimeout(() => {
      proactiveTimerRef.current = null;
      void preOpen();
    }, PROACTIVE_SWAP_MS);
  }, [clearProactiveTimer, preOpen]);

  // Keep the ref in sync so promote/onopen can re-arm without a declaration cycle.
  useEffect(() => {
    armProactiveRef.current = armProactive;
  }, [armProactive]);

  /**
   * Open (or reopen) the active socket. Tears down any dangling active socket
   * first, re-presigns, binds via activeRef, and re-sends hello on open.
   */
  const connectInternal = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (sessionId === null) return;

    // Tear down any existing active socket so we never leave a dangling handler.
    if (activeRef.current) {
      teardownConnection(activeRef.current, true);
      activeRef.current = null;
    }
    clearProactiveTimer();

    setStatus((s) => (s === 'reconnecting' ? s : 'connecting'));

    let wsUrl: string;
    try {
      ({ wsUrl } = await getPresignedWsUrl(sessionId));
    } catch {
      setStatus('reconnecting');
      scheduleReactiveReconnect();
      return;
    }
    if (intentionalCloseRef.current) return;

    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    const conn: VoiceConnection = {
      ws,
      pingTimer: null,
      live: false,
      role: 'active',
      firstInboundAudio: false,
      settleElapsed: false,
      settleTimer: null,
      intentional: false,
    };
    activeRef.current = conn;
    wireConnection(conn);
  }, [teardownConnection, clearProactiveTimer, scheduleReactiveReconnect, wireConnection]);

  useEffect(() => {
    connectInternalRef.current = connectInternal;
  }, [connectInternal]);

  const stop = useCallback(() => {
    intentionalCloseRef.current = true;
    clearReconnectTimer();
    clearProactiveTimer();
    clearPreOpenTimeout();
    if (activeRef.current) {
      teardownConnection(activeRef.current, true);
      activeRef.current = null;
    }
    if (standbyRef.current) {
      teardownConnection(standbyRef.current, true);
      standbyRef.current = null;
    }
    speakingUnsubRef.current?.();
    speakingUnsubRef.current = null;
    micRef.current?.stop();
    playerRef.current?.stop();
    micRef.current = null;
    playerRef.current = null;
    setSpeaking(false);
    setStatus('ended');
  }, [clearReconnectTimer, clearProactiveTimer, clearPreOpenTimeout, teardownConnection]);

  const start = useCallback(
    async (sessionId: string) => {
      // A fresh start is intentional: allow reconnects again and reset backoff.
      intentionalCloseRef.current = false;
      backoffRef.current = INITIAL_BACKOFF_MS;
      sessionIdRef.current = sessionId;
      clearReconnectTimer();
      setStatus('connecting');
      setError(null);

      // Create the player + mic ONCE and keep them alive across socket swaps.
      // sendMic targets the live active socket (or buffers), so a swap never
      // re-prompts getUserMedia.
      if (!playerRef.current) {
        const player = new AudioPlayer();
        playerRef.current = player;
        // Mirror the player's speaking signal into React state (talking logo).
        speakingUnsubRef.current = player.onSpeakingChange(setSpeaking);
      }
      if (!micRef.current) {
        const mic = new MicCapture((pcm16) => sendMic(pcm16));
        micRef.current = mic;
        try {
          await mic.start();
        } catch (e) {
          micRef.current = null;
          setError(`mic error: ${(e as Error).message}`);
          setStatus('error');
          return;
        }
      }

      await connectInternal();
    },
    [clearReconnectTimer, connectInternal, sendMic],
  );

  // On unmount, deliberately close and clean everything up so we never
  // reconnect after the component is gone (mirrors the screenshare hook).
  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      clearReconnectTimer();
      clearProactiveTimer();
      clearPreOpenTimeout();
      if (activeRef.current) {
        teardownConnection(activeRef.current, true);
        activeRef.current = null;
      }
      if (standbyRef.current) {
        teardownConnection(standbyRef.current, true);
        standbyRef.current = null;
      }
      speakingUnsubRef.current?.();
      speakingUnsubRef.current = null;
      micRef.current?.stop();
      playerRef.current?.stop();
      micRef.current = null;
      playerRef.current = null;
    };
  }, [clearReconnectTimer, clearProactiveTimer, clearPreOpenTimeout, teardownConnection]);

  return { status, error, speaking, start, stop };
}
